import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfMonth, endOfMonth, format, subMonths } from 'date-fns';
import { parseDate } from '@/lib/localizedDateUtils';

export interface SourceDetail {
  sourceType: 'budget' | 'recurring_expense' | 'employee' | 'receivable' | 'history' | 'sales_goal';
  sourceId: string;
  sourceName: string;
  amount: number;
}

export interface DreLineItem {
  accountCode: string;
  accountName: string;
  category: string;
  amounts: Record<string, number>;
  amountsByBu?: {
    us: Record<string, number>;
    br: Record<string, number>;
  };
  total: number;
  totalByBu?: {
    us: number;
    br: number;
  };
  type: 'revenue' | 'expense';
  sourceDetails?: Record<string, SourceDetail[]>;
}

export interface DreSection {
  name: string;
  icon: string;
  type: 'revenue' | 'cac' | 'fulfillment' | 'ga' | 'financial';
  items: DreLineItem[];
  subtotal: Record<string, number>;
  subtotalByBu?: {
    us: Record<string, number>;
    br: Record<string, number>;
  };
  total: number;
  totalByBu?: {
    us: number;
    br: number;
  };
}

export interface DreSummary {
  totalRevenue: Record<string, number>;
  totalCac: Record<string, number>;
  totalFulfillment: Record<string, number>;
  totalGa: Record<string, number>;
  totalFinancial: Record<string, number>;
  grossProfit: Record<string, number>;
  marginContribution: Record<string, number>;
  ebitda: Record<string, number>;
  netProfit: Record<string, number>;
  grossMargin: Record<string, number>;             // Margem Bruta % (sobre Receita Bruta)
  marginContributionPercent: Record<string, number>; // Margem Contribuição % (sobre Receita Bruta)
  ebitdaMargin: Record<string, number>;            // Margem EBITDA % (sobre Receita Bruta)
  netMargin: Record<string, number>;               // Margem Líquida % (sobre Receita Bruta)
}

export interface DreSummaryByBu {
  us: DreSummary;
  br: DreSummary;
}

export interface DreGrandTotals {
  revenue: number;
  cac: number;
  fulfillment: number;
  ga: number;
  financial: number;
  grossProfit: number;
  marginContribution: number;
  ebitda: number;
  netProfit: number;
  grossMargin: number;                 // Margem Bruta % (sobre Receita Bruta)
  marginContributionPercent: number;   // Margem Contribuição % (sobre Receita Bruta)
  ebitdaMargin: number;                // Margem EBITDA % (sobre Receita Bruta)
  netMargin: number;                   // Margem Líquida % (sobre Receita Bruta)
}

export interface DreData {
  sections: DreSection[];
  months: string[];
  summary: DreSummary;
  summaryByBu?: DreSummaryByBu;
  grandTotals: DreGrandTotals;
  grandTotalsByBu?: {
    us: DreGrandTotals;
    br: DreGrandTotals;
  };
  revenueRatios?: {
    us: number;
    br: number;
  };
}

type PeriodType = 'ytd' | '12m' | '6m' | '3m' | 'custom';

interface UseDreRealizadoParams {
  period: PeriodType;
  customStartDate?: Date;
  customEndDate?: Date;
  businessUnit?: string;
}

function getDateRange(period: PeriodType, customStart?: Date, customEnd?: Date): { start: Date; end: Date } {
  const now = new Date();
  const endDate = endOfMonth(now);
  
  switch (period) {
    case 'ytd':
      return { start: new Date(now.getFullYear(), 0, 1), end: endDate };
    case '12m':
      return { start: startOfMonth(subMonths(now, 11)), end: endDate };
    case '6m':
      return { start: startOfMonth(subMonths(now, 5)), end: endDate };
    case '3m':
      return { start: startOfMonth(subMonths(now, 2)), end: endDate };
    case 'custom':
      return { 
        start: customStart || startOfMonth(subMonths(now, 11)), 
        end: customEnd || endDate 
      };
    default:
      return { start: startOfMonth(subMonths(now, 11)), end: endDate };
  }
}

function generateMonthsList(start: Date, end: Date): string[] {
  const months: string[] = [];
  let current = startOfMonth(start);
  const endMonth = endOfMonth(end);
  
  while (current <= endMonth) {
    months.push(format(current, 'yyyy-MM'));
    current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
  }
  
  return months;
}

function getCategoryForAccount(code: string): 'revenue' | 'cac' | 'fulfillment' | 'ga' | 'financial' | 'other' {
  const prefix = code.substring(0, 1);
  
  if (prefix === '4') return 'revenue';
  if (prefix === '5') return 'cac';
  if (prefix === '6') return 'fulfillment';
  if (prefix === '7') return 'ga';
  if (prefix === '8' || prefix === '9') return 'financial';
  
  return 'other';
}

function initializeMonthlyRecord(months: string[]): Record<string, number> {
  const record: Record<string, number> = {};
  months.forEach(m => { record[m] = 0; });
  return record;
}

function createEmptySummary(months: string[]): DreSummary {
  return {
    totalRevenue: initializeMonthlyRecord(months),
    totalCac: initializeMonthlyRecord(months),
    totalFulfillment: initializeMonthlyRecord(months),
    totalGa: initializeMonthlyRecord(months),
    totalFinancial: initializeMonthlyRecord(months),
    grossProfit: initializeMonthlyRecord(months),
    marginContribution: initializeMonthlyRecord(months),
    ebitda: initializeMonthlyRecord(months),
    netProfit: initializeMonthlyRecord(months),
    grossMargin: initializeMonthlyRecord(months),
    marginContributionPercent: initializeMonthlyRecord(months),
    ebitdaMargin: initializeMonthlyRecord(months),
    netMargin: initializeMonthlyRecord(months),
  };
}

function createEmptyGrandTotals(): DreGrandTotals {
  return {
    revenue: 0,
    cac: 0,
    fulfillment: 0,
    ga: 0,
    financial: 0,
    grossProfit: 0,
    marginContribution: 0,
    ebitda: 0,
    netProfit: 0,
    grossMargin: 0,
    marginContributionPercent: 0,
    ebitdaMargin: 0,
    netMargin: 0,
  };
}

export function useDreRealizado({ period, customStartDate, customEndDate, businessUnit }: UseDreRealizadoParams) {
  return useQuery({
    queryKey: ['dre-realizado', period, customStartDate?.toISOString(), customEndDate?.toISOString(), businessUnit],
    queryFn: async (): Promise<DreData> => {
      const { start, end } = getDateRange(period, customStartDate, customEndDate);
      const months = generateMonthsList(start, end);
      const isConsolidated = !businessUnit || businessUnit === 'all';
      
      // 1. Fetch revenues (paid payments) - always fetch all to calculate ratios
      const { data: revenues } = await supabase
        .from('payments')
        .select('amount, paid_at, deal:deals(business_unit, currency)')
        .eq('status', 'paid')
        .gte('paid_at', format(start, 'yyyy-MM-dd'))
        .lte('paid_at', format(end, 'yyyy-MM-dd'));
      
      // 2. Fetch ALL expenses (we need shared expenses for G&A rateio)
      const { data: expenses } = await supabase
        .from('expenses')
        .select('amount, currency, paid_at, account_code, description, business_unit')
        .eq('status', 'paid')
        .gte('paid_at', format(start, 'yyyy-MM-dd'))
        .lte('paid_at', format(end, 'yyyy-MM-dd'));
      
      // 3. Fetch chart of accounts
      const { data: accounts } = await supabase
        .from('chart_of_accounts')
        .select('*')
        .order('code');
      
      // 3b. Fetch exchange rate for BRL to USD
      const { data: latestRate } = await supabase
        .from('exchange_rates')
        .select('rate')
        .eq('from_currency', 'BRL')
        .eq('to_currency', 'USD')
        .order('effective_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      // Exchange rate: if rate is 0.18 (1 BRL = 0.18 USD), divide BRL by (1/rate) = multiply by rate
      // If rate is stored as 5.5 (1 USD = 5.5 BRL), then 1 BRL = 1/5.5 USD, so divide by 5.5
      const exchangeRate = latestRate?.rate ? (1 / latestRate.rate) : 5.5;
      
      // 4. Calculate revenue by BU and month for ratio calculation
      const revenueByBuAndMonth: { us: Record<string, number>; br: Record<string, number> } = {
        us: initializeMonthlyRecord(months),
        br: initializeMonthlyRecord(months),
      };
      let totalRevenueUs = 0;
      let totalRevenueBr = 0;
      
      revenues?.forEach(r => {
        if (!r.paid_at) return;
        const dealBu = (r.deal as any)?.business_unit || 'us';
        const dealCurrency = (r.deal as any)?.currency || 'USD';
        const paidDate = parseDate(r.paid_at.substring(0, 10));
        if (!paidDate) return;
        const month = format(paidDate, 'yyyy-MM');
        if (!months.includes(month)) return;
        
        // Convert BRL to USD if needed
        const rawAmount = Number(r.amount);
        const amount = dealCurrency === 'BRL' ? rawAmount / exchangeRate : rawAmount;
        
        if (dealBu === 'us') {
          revenueByBuAndMonth.us[month] += amount;
          totalRevenueUs += amount;
        } else {
          revenueByBuAndMonth.br[month] += amount;
          totalRevenueBr += amount;
        }
      });
      
      // Calculate revenue ratios for G&A allocation
      const totalRevenue = totalRevenueUs + totalRevenueBr;
      const revenueRatios = {
        us: totalRevenue > 0 ? totalRevenueUs / totalRevenue : 0.5,
        br: totalRevenue > 0 ? totalRevenueBr / totalRevenue : 0.5,
      };
      
      // 5. Group expenses by account, month, and BU
      // Structure: { accountCode: { month: { us: amount, br: amount, shared: amount } } }
      const expensesByAccountMonthBu: Record<string, Record<string, { us: number; br: number; shared: number }>> = {};
      
      expenses?.forEach(e => {
        if (!e.paid_at) return;
        const code = e.account_code || '7500';
        const paidDate = parseDate(e.paid_at.substring(0, 10));
        if (!paidDate) return;
        const month = format(paidDate, 'yyyy-MM');
        if (!months.includes(month)) return;
        
        if (!expensesByAccountMonthBu[code]) {
          expensesByAccountMonthBu[code] = {};
        }
        if (!expensesByAccountMonthBu[code][month]) {
          expensesByAccountMonthBu[code][month] = { us: 0, br: 0, shared: 0 };
        }
        
        // Convert BRL to USD if needed
        const rawAmount = Number(e.amount);
        const amount = e.currency === 'BRL' ? rawAmount / exchangeRate : rawAmount;
        const expenseBu = e.business_unit;
        
        if (!expenseBu) {
          // Shared expense (null business_unit)
          expensesByAccountMonthBu[code][month].shared += amount;
        } else if (expenseBu === 'us') {
          expensesByAccountMonthBu[code][month].us += amount;
        } else {
          expensesByAccountMonthBu[code][month].br += amount;
        }
      });
      
      // 6. Build DRE structure
      const accountMap = new Map(accounts?.map(a => [a.code, a]) || []);
      
      const sections: DreSection[] = [
        { name: 'Receita Bruta', icon: '📈', type: 'revenue', items: [], subtotal: {}, subtotalByBu: { us: {}, br: {} }, total: 0, totalByBu: { us: 0, br: 0 } },
        { name: '(-) CSP - Custo dos Serviços', icon: '📦', type: 'fulfillment', items: [], subtotal: {}, subtotalByBu: { us: {}, br: {} }, total: 0, totalByBu: { us: 0, br: 0 } },
        { name: '(-) CAC - Custo de Aquisição', icon: '📣', type: 'cac', items: [], subtotal: {}, subtotalByBu: { us: {}, br: {} }, total: 0, totalByBu: { us: 0, br: 0 } },
        { name: '(-) G&A - Despesas Administrativas', icon: '🏢', type: 'ga', items: [], subtotal: {}, subtotalByBu: { us: {}, br: {} }, total: 0, totalByBu: { us: 0, br: 0 } },
        { name: '(-) Despesas Financeiras', icon: '💳', type: 'financial', items: [], subtotal: {}, subtotalByBu: { us: {}, br: {} }, total: 0, totalByBu: { us: 0, br: 0 } },
      ];
      
      const sectionMap: Record<string, number> = {
        revenue: 0,
        fulfillment: 1,  // CSP - index 1
        cac: 2,          // CAC - index 2
        ga: 3,
        financial: 4,
      };
      
      // Initialize subtotals
      sections.forEach(s => {
        months.forEach(m => {
          s.subtotal[m] = 0;
          s.subtotalByBu!.us[m] = 0;
          s.subtotalByBu!.br[m] = 0;
        });
      });
      
      // Process revenues
      const revenueItem: DreLineItem = {
        accountCode: '4100',
        accountName: accountMap.get('4100')?.name || 'Receita de Serviços',
        category: 'revenue',
        amounts: {},
        amountsByBu: { us: {}, br: {} },
        total: totalRevenue,
        totalByBu: { us: totalRevenueUs, br: totalRevenueBr },
        type: 'revenue',
      };
      
      months.forEach(m => {
        const usAmount = revenueByBuAndMonth.us[m] || 0;
        const brAmount = revenueByBuAndMonth.br[m] || 0;
        revenueItem.amounts[m] = usAmount + brAmount;
        revenueItem.amountsByBu!.us[m] = usAmount;
        revenueItem.amountsByBu!.br[m] = brAmount;
        sections[0].subtotal[m] = usAmount + brAmount;
        sections[0].subtotalByBu!.us[m] = usAmount;
        sections[0].subtotalByBu!.br[m] = brAmount;
      });
      
      sections[0].items.push(revenueItem);
      sections[0].total = totalRevenue;
      sections[0].totalByBu = { us: totalRevenueUs, br: totalRevenueBr };
      
      // Process expenses with G&A rateio
      Object.entries(expensesByAccountMonthBu).forEach(([code, monthData]) => {
        const account = accountMap.get(code);
        const category = getCategoryForAccount(code);
        const sectionIdx = sectionMap[category] ?? 3;
        const isGa = category === 'ga';
        
        const item: DreLineItem = {
          accountCode: code,
          accountName: account?.name || code,
          category,
          amounts: {},
          amountsByBu: { us: {}, br: {} },
          total: 0,
          totalByBu: { us: 0, br: 0 },
          type: 'expense',
        };
        
        let itemTotalUs = 0;
        let itemTotalBr = 0;
        
        months.forEach(m => {
          const data = monthData[m] || { us: 0, br: 0, shared: 0 };
          let usAmount = data.us;
          let brAmount = data.br;
          
          // G&A rateio: distribute shared costs proportionally to revenue
          if (isGa && data.shared > 0) {
            usAmount += data.shared * revenueRatios.us;
            brAmount += data.shared * revenueRatios.br;
          } else if (data.shared > 0) {
            // For non-G&A, shared costs go 50/50 (fallback)
            usAmount += data.shared * 0.5;
            brAmount += data.shared * 0.5;
          }
          
          const totalAmount = usAmount + brAmount;
          
          item.amounts[m] = totalAmount;
          item.amountsByBu!.us[m] = usAmount;
          item.amountsByBu!.br[m] = brAmount;
          
          itemTotalUs += usAmount;
          itemTotalBr += brAmount;
          
          sections[sectionIdx].subtotal[m] += totalAmount;
          sections[sectionIdx].subtotalByBu!.us[m] += usAmount;
          sections[sectionIdx].subtotalByBu!.br[m] += brAmount;
        });
        
        item.total = itemTotalUs + itemTotalBr;
        item.totalByBu = { us: itemTotalUs, br: itemTotalBr };
        
        sections[sectionIdx].items.push(item);
        sections[sectionIdx].total += item.total;
        sections[sectionIdx].totalByBu!.us += itemTotalUs;
        sections[sectionIdx].totalByBu!.br += itemTotalBr;
      });
      
      // Sort items by code
      sections.forEach(s => s.items.sort((a, b) => a.accountCode.localeCompare(b.accountCode)));
      
      // Calculate summaries for consolidated and by BU
      const summary = createEmptySummary(months);
      const summaryByBu: DreSummaryByBu = {
        us: createEmptySummary(months),
        br: createEmptySummary(months),
      };
      
      months.forEach(m => {
        // Consolidated - New structure: Revenue -> CSP -> Gross Profit -> CAC -> Margin Contribution -> G&A -> EBITDA -> Financial -> Net Profit
        const revenue = sections[0].subtotal[m] || 0;
        const csp = sections[1].subtotal[m] || 0;       // CSP (Fulfillment)
        const cac = sections[2].subtotal[m] || 0;
        const ga = sections[3].subtotal[m] || 0;
        const financial = sections[4].subtotal[m] || 0;
        
        const grossProfit = revenue - csp;
        const marginContribution = grossProfit - cac;
        const ebitda = marginContribution - ga;
        const netProfit = ebitda - financial;
        
        summary.totalRevenue[m] = revenue;
        summary.totalFulfillment[m] = csp;
        summary.totalCac[m] = cac;
        summary.totalGa[m] = ga;
        summary.totalFinancial[m] = financial;
        summary.grossProfit[m] = grossProfit;
        summary.marginContribution[m] = marginContribution;
        summary.ebitda[m] = ebitda;
        summary.netProfit[m] = netProfit;
        // All margins calculated over Gross Revenue (Receita Bruta) - accounting standard
        summary.grossMargin[m] = revenue > 0 ? (grossProfit / revenue) * 100 : 0;
        summary.marginContributionPercent[m] = revenue > 0 ? (marginContribution / revenue) * 100 : 0;
        summary.ebitdaMargin[m] = revenue > 0 ? (ebitda / revenue) * 100 : 0;
        summary.netMargin[m] = revenue > 0 ? (netProfit / revenue) * 100 : 0;
        
        // By BU - US
        const revenueUs = sections[0].subtotalByBu?.us[m] || 0;
        const cspUs = sections[1].subtotalByBu?.us[m] || 0;
        const cacUs = sections[2].subtotalByBu?.us[m] || 0;
        const gaUs = sections[3].subtotalByBu?.us[m] || 0;
        const financialUs = sections[4].subtotalByBu?.us[m] || 0;
        
        const grossProfitUs = revenueUs - cspUs;
        const marginContributionUs = grossProfitUs - cacUs;
        const ebitdaUs = marginContributionUs - gaUs;
        const netProfitUs = ebitdaUs - financialUs;
        
        summaryByBu.us.totalRevenue[m] = revenueUs;
        summaryByBu.us.totalFulfillment[m] = cspUs;
        summaryByBu.us.totalCac[m] = cacUs;
        summaryByBu.us.totalGa[m] = gaUs;
        summaryByBu.us.totalFinancial[m] = financialUs;
        summaryByBu.us.grossProfit[m] = grossProfitUs;
        summaryByBu.us.marginContribution[m] = marginContributionUs;
        summaryByBu.us.ebitda[m] = ebitdaUs;
        summaryByBu.us.netProfit[m] = netProfitUs;
        // All margins calculated over Gross Revenue (Receita Bruta) - accounting standard
        summaryByBu.us.grossMargin[m] = revenueUs > 0 ? (grossProfitUs / revenueUs) * 100 : 0;
        summaryByBu.us.marginContributionPercent[m] = revenueUs > 0 ? (marginContributionUs / revenueUs) * 100 : 0;
        summaryByBu.us.ebitdaMargin[m] = revenueUs > 0 ? (ebitdaUs / revenueUs) * 100 : 0;
        summaryByBu.us.netMargin[m] = revenueUs > 0 ? (netProfitUs / revenueUs) * 100 : 0;
        
        // By BU - BR
        const revenueBr = sections[0].subtotalByBu?.br[m] || 0;
        const cspBr = sections[1].subtotalByBu?.br[m] || 0;
        const cacBr = sections[2].subtotalByBu?.br[m] || 0;
        const gaBr = sections[3].subtotalByBu?.br[m] || 0;
        const financialBr = sections[4].subtotalByBu?.br[m] || 0;
        
        const grossProfitBr = revenueBr - cspBr;
        const marginContributionBr = grossProfitBr - cacBr;
        const ebitdaBr = marginContributionBr - gaBr;
        const netProfitBr = ebitdaBr - financialBr;
        
        summaryByBu.br.totalRevenue[m] = revenueBr;
        summaryByBu.br.totalFulfillment[m] = cspBr;
        summaryByBu.br.totalCac[m] = cacBr;
        summaryByBu.br.totalGa[m] = gaBr;
        summaryByBu.br.totalFinancial[m] = financialBr;
        summaryByBu.br.grossProfit[m] = grossProfitBr;
        summaryByBu.br.marginContribution[m] = marginContributionBr;
        summaryByBu.br.ebitda[m] = ebitdaBr;
        summaryByBu.br.netProfit[m] = netProfitBr;
        // All margins calculated over Gross Revenue (Receita Bruta) - accounting standard
        summaryByBu.br.grossMargin[m] = revenueBr > 0 ? (grossProfitBr / revenueBr) * 100 : 0;
        summaryByBu.br.marginContributionPercent[m] = revenueBr > 0 ? (marginContributionBr / revenueBr) * 100 : 0;
        summaryByBu.br.ebitdaMargin[m] = revenueBr > 0 ? (ebitdaBr / revenueBr) * 100 : 0;
        summaryByBu.br.netMargin[m] = revenueBr > 0 ? (netProfitBr / revenueBr) * 100 : 0;
      });
      
      // Calculate grand totals - New structure
      const calculateGrandTotals = (sec: DreSection[], getBuValue: (s: DreSection) => number): DreGrandTotals => {
        const revenue = getBuValue(sec[0]);
        const csp = getBuValue(sec[1]);       // CSP (Fulfillment)
        const cac = getBuValue(sec[2]);
        const ga = getBuValue(sec[3]);
        const financial = getBuValue(sec[4]);
        
        const grossProfit = revenue - csp;
        const marginContribution = grossProfit - cac;
        const ebitda = marginContribution - ga;
        const netProfit = ebitda - financial;
        
        return {
          revenue,
          cac,
          fulfillment: csp,
          ga,
          financial,
          grossProfit,
          marginContribution,
          ebitda,
          netProfit,
          // All margins calculated over Gross Revenue (Receita Bruta) - accounting standard
          grossMargin: revenue > 0 ? (grossProfit / revenue) * 100 : 0,
          marginContributionPercent: revenue > 0 ? (marginContribution / revenue) * 100 : 0,
          ebitdaMargin: revenue > 0 ? (ebitda / revenue) * 100 : 0,
          netMargin: revenue > 0 ? (netProfit / revenue) * 100 : 0,
        };
      };
      
      const grandTotals = calculateGrandTotals(sections, s => s.total);
      const grandTotalsByBu = {
        us: calculateGrandTotals(sections, s => s.totalByBu?.us || 0),
        br: calculateGrandTotals(sections, s => s.totalByBu?.br || 0),
      };
      
      // If filtering by specific BU, return only that BU's data
      if (!isConsolidated) {
        const targetBu = businessUnit === 'usa' ? 'us' : 'br';
        
        // Filter sections to show only the target BU values
        const filteredSections = sections.map(s => ({
          ...s,
          subtotal: s.subtotalByBu?.[targetBu] || {},
          total: s.totalByBu?.[targetBu] || 0,
          items: s.items.map(item => ({
            ...item,
            amounts: item.amountsByBu?.[targetBu] || {},
            total: item.totalByBu?.[targetBu] || 0,
          })),
        }));
        
        return {
          sections: filteredSections,
          months,
          summary: summaryByBu[targetBu],
          grandTotals: grandTotalsByBu[targetBu],
          revenueRatios,
        };
      }
      
      return {
        sections,
        months,
        summary,
        summaryByBu,
        grandTotals,
        grandTotalsByBu,
        revenueRatios,
      };
    },
  });
}
